
import { useState } from 'react';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { UserRole } from '@/types/auth';

interface ActivationResult {
  success: boolean;
  staff_id?: string;
  role?: string;
  account_id?: string;
  error?: string;
}

interface InviteUserResult {
  success: boolean;
  invitation_token?: string;
  email?: string;
  role?: string;
  error?: string;
}

export const useStaffInvitation = () => {
  const [isLoading, setIsLoading] = useState(false);

  const inviteUser = async (email: string, role: UserRole) => {
    try {
      setIsLoading(true);

      // Map UserRole to expected staff role types
      const staffRole = role === 'admin' ? 'admin' : 'manager';

      const { data, error } = await supabaseWorker
        .rpc('invite_staff_member', {
          email_param: email,
          role_param: staffRole,
          account_id_param: null // Will be set by the function based on current user
        });

      if (error) {
        throw error;
      }

      const result = data as unknown as InviteUserResult;

      if (result && typeof result === 'object' && result.success) {
        return { success: true, data: result };
      } else {
        const errorMessage = result?.error || 'Ошибка при отправке приглашения';
        throw new Error(errorMessage);
      }
    } catch (error: unknown) {
      console.error('Error inviting user:', error);
      const message = error instanceof Error ? error.message : 'Не удалось отправить приглашение';
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  const activateInvitation = async (invitationToken: string, userId: string) => {
    try {
      setIsLoading(true);

      const { data, error } = await supabaseWorker
        .rpc('activate_staff_invitation', {
          invitation_token_param: invitationToken,
          user_id_param: userId
        });

      if (error) {
        throw error;
      }

      const result = data as unknown as ActivationResult;

      if (result && typeof result === 'object' && result.success) {
        return { success: true, data: result };
      } else {
        const errorMessage = result?.error || 'Ошибка при активации приглашения';
        return { success: false, error: errorMessage };
      }
    } catch (error: unknown) {
      console.error('Error activating invitation:', error);
      const message = error instanceof Error ? error.message : 'Не удалось активировать приглашение';
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    inviteUser,
    activateInvitation,
    isLoading
  };
};
